/**
 * Chart Modal - Probability density chart gösterimi için modal bileşeni
 * 
 * Features:
 * - Probability density visualization
 * - Winner probability analysis
 * - Expected values display
 * - Export functionality
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Paper
} from '@mui/material';
import {
  GetApp as ExportIcon
} from '@mui/icons-material';
import { Line } from 'react-chartjs-2';
import {
  formatPercentage
} from '../../utils/statisticsUtils';

const ChartModal = ({
  selectedVariantChart,
  onClose,
  chartOptions
}) => {
  if (!selectedVariantChart) return null;

  const { variant, chartData } = selectedVariantChart;

  const handleExport = () => {
    // Export chart data
    const results = {
      variant: variant.name,
      chartData: chartData,
      analysis: variant.enhancedBayesian
    };
    const dataStr = JSON.stringify(results, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `probability-density-${variant.name}.json`;
    link.click();
    
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog 
      open={Boolean(selectedVariantChart)} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        Probability Density Distribution - {variant?.name}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ height: 400, mt: 2 }}>
          {chartData && (
            <Line 
              data={chartData} 
              options={chartOptions} 
            />
          )}
        </Box>
        
        {/* Winner Probability Info */}
        {variant?.enhancedBayesian?.winnerProbabilities && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Winner Probability Analysis
            </Typography>
            <Grid container spacing={2}>
              {variant.enhancedBayesian.winnerProbabilities.map((prob, index) => (
                <Grid item xs={6} key={index}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" color={prob.isControl ? 'primary' : 'secondary'}>
                      {formatPercentage(prob.winProbability)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {prob.name}
                      {prob.isControl && ' (Control)'}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Expected Values */}
        {variant?.enhancedBayesian?.expectedValues && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Expected Values
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h5" color="primary">
                    {formatPercentage(variant.enhancedBayesian.expectedValues.control)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Control Expected CR
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={4}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h5" color="secondary">
                    {formatPercentage(variant.enhancedBayesian.expectedValues.variant)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Variant Expected CR
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={4}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography 
                    variant="h5" 
                    color={variant.enhancedBayesian.expectedValues.lift > 0 ? 'success.main' : 'error.main'}
                  >
                    {variant.enhancedBayesian.expectedValues.lift > 0 ? '+' : ''}
                    {formatPercentage(variant.enhancedBayesian.expectedValues.lift)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Expected Lift
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          Kapat
        </Button>
        <Button 
          variant="contained"
          onClick={handleExport}
          startIcon={<ExportIcon />}
        >
          Dışa Aktar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChartModal; 